import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Youtube } from "lucide-react";

export default function SubmitClipPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    youtube_url: "",
    comedian: "",
    category: "standup",
    era: "modern",
    style: "observational",
    description: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="font-display text-4xl text-white">SIGN IN TO SUBMIT</h1>
        <p className="mt-2 text-slate-400">You need an account to submit clips.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button data-testid="submit-goto-login" onClick={() => navigate("/login")} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Sign in</Button>
          <Button variant="outline" onClick={() => navigate("/register")} className="border-white/20">Register</Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/clips", form);
      toast.success("Clip submitted! Let the voting begin.");
      navigate(`/clip/${data.id}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 pb-24 md:pb-12">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/50 bg-amber-400/10 px-3 py-1 text-[10px] font-mono-ui uppercase tracking-widest text-amber-400">
          <Plus className="h-3 w-3" /> New submission
        </div>
        <h1 className="mt-4 font-display text-5xl text-white">DROP A <span className="text-amber-400">BANGER</span></h1>
        <p className="mt-2 text-slate-400">Paste a YouTube link. The community handles the rest.</p>
      </div>

      <form onSubmit={submit} className="space-y-6 glass rounded-2xl p-6 sm:p-8">
        <Field label="YouTube URL" icon={<Youtube className="h-4 w-4 text-[#FF2E55]" />}>
          <Input
            required
            data-testid="submit-youtube-url"
            placeholder="https://youtube.com/watch?v=..."
            value={form.youtube_url}
            onChange={update("youtube_url")}
            className="bg-black/40 border-white/10 focus:border-amber-400"
          />
        </Field>

        <Field label="Title">
          <Input
            required
            data-testid="submit-title"
            placeholder="e.g. Kevin Hart on Riding Horses"
            value={form.title}
            onChange={update("title")}
            className="bg-black/40 border-white/10 focus:border-amber-400"
          />
        </Field>

        <Field label="Comedian">
          <Input
            required
            data-testid="submit-comedian"
            placeholder="e.g. John Mulaney"
            value={form.comedian}
            onChange={update("comedian")}
            className="bg-black/40 border-white/10 focus:border-amber-400"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Category">
            <Select value={form.category} onValueChange={update("category")}>
              <SelectTrigger data-testid="submit-category" className="bg-black/40 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141C] border-white/10 text-white">
                <SelectItem value="standup">Stand-up</SelectItem>
                <SelectItem value="skit">Skit</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Era">
            <Select value={form.era} onValueChange={update("era")}>
              <SelectTrigger data-testid="submit-era" className="bg-black/40 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141C] border-white/10 text-white">
                <SelectItem value="classic">Classic</SelectItem>
                <SelectItem value="90s">90s</SelectItem>
                <SelectItem value="2000s">2000s</SelectItem>
                <SelectItem value="modern">Modern</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Style">
            <Select value={form.style} onValueChange={update("style")}>
              <SelectTrigger data-testid="submit-style" className="bg-black/40 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141C] border-white/10 text-white">
                {["crowd_work","dark","physical","musical","satire","storytelling","observational","absurd"].map(s =>
                  <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Description (optional)">
          <Textarea
            data-testid="submit-description"
            placeholder="What makes this clip a killer?"
            value={form.description}
            onChange={update("description")}
            className="bg-black/40 border-white/10 focus:border-amber-400 min-h-[100px]"
          />
        </Field>

        {error && <p className="text-sm text-[#FF2E55]" data-testid="submit-error">{error}</p>}

        <Button
          type="submit"
          disabled={busy}
          data-testid="submit-clip-btn"
          className="w-full bg-amber-400 text-black hover:bg-amber-300 font-bold text-base tracking-widest uppercase btn-brutal"
        >
          {busy ? "Submitting..." : "Submit Clip"}
        </Button>
      </form>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-widest text-slate-300">
        {icon}{label}
      </Label>
      {children}
    </div>
  );
}
